import React, { useState, useEffect } from 'react'
import { Building2 } from 'lucide-react'

interface StockLogoProps {
  symbol: string
  className?: string
  size?: number
  fallbackIcon?: React.ReactNode
  currency?: string
}

// Shared across all StockLogo instances so a symbol is only ever requested
// once per cache window, instead of once per rendered row (avoids 429s from
// LogoKit's rate limit when a list renders many tickers at once).
const CACHE_STORAGE_KEY = 'logokit_cache_v1'
const SUCCESS_TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days
const FAILURE_TTL_MS = 60 * 60 * 1000 // 1 hour - allow retry once rate limit clears

interface CacheEntry {
  url: string | null
  ts: number
}

const memoryCache = new Map<string, CacheEntry>()
const inFlight = new Map<string, Promise<string | null>>()

// Throttle how fast logo requests go out - rendering a long list (watchlist,
// alpha picks) fires one request per distinct symbol all at once, which blows
// past LogoKit's rate limit even with the dedup/cache above.
const MAX_CONCURRENT = 3
const MIN_START_INTERVAL_MS = 250
let activeCount = 0
let lastStart = 0
const startQueue: Array<() => void> = []

function drainStartQueue() {
  if (activeCount >= MAX_CONCURRENT || startQueue.length === 0) return
  const wait = Math.max(0, lastStart + MIN_START_INTERVAL_MS - Date.now())
  setTimeout(() => {
    const next = startQueue.shift()
    if (!next) return
    lastStart = Date.now()
    activeCount++
    next()
    drainStartQueue()
  }, wait)
}

function scheduleStart(run: () => void) {
  startQueue.push(run)
  drainStartQueue()
}

function loadCacheEntry(key: string): CacheEntry | undefined {
  const cached = memoryCache.get(key)
  if (cached) return cached

  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY)
    if (!raw) return undefined
    const all = JSON.parse(raw) as Record<string, CacheEntry>
    const entry = all[key]
    if (entry) memoryCache.set(key, entry)
    return entry
  } catch {
    return undefined
  }
}

function saveCacheEntry(key: string, entry: CacheEntry) {
  memoryCache.set(key, entry)
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY)
    const all = raw ? (JSON.parse(raw) as Record<string, CacheEntry>) : {}
    all[key] = entry
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(all))
  } catch {
    // localStorage unavailable/full - memory cache still works for this session
  }
}

function isFresh(entry: CacheEntry): boolean {
  const ttl = entry.url ? SUCCESS_TTL_MS : FAILURE_TTL_MS
  return Date.now() - entry.ts < ttl
}

function buildCacheKey(symbol: string, currency: string): string {
  let cleanSymbol = symbol.replace(/\.(BK|SET|TH)$/i, '')
  if (currency === 'THB') cleanSymbol = `${cleanSymbol}.BK`
  return cleanSymbol
}

function getFreshCachedEntry(symbol: string, currency: string): CacheEntry | undefined {
  if (!symbol) return undefined
  const entry = loadCacheEntry(buildCacheKey(symbol, currency))
  return entry && isFresh(entry) ? entry : undefined
}

function fetchLogoUrl(cacheKey: string, logoKitUrl: string): Promise<string | null> {
  const existing = inFlight.get(cacheKey)
  if (existing) return existing

  const promise = new Promise<string | null>((resolve) => {
    scheduleStart(() => {
      const img = new Image()
      let settled = false

      const finish = (url: string | null) => {
        if (settled) return
        settled = true
        saveCacheEntry(cacheKey, { url, ts: Date.now() })
        activeCount--
        drainStartQueue()
        resolve(url)
      }

      img.onload = () => finish(logoKitUrl)
      img.onerror = () => finish(null)
      img.src = logoKitUrl

      setTimeout(() => finish(null), 5000)
    })
  }).finally(() => {
    inFlight.delete(cacheKey)
  })

  inFlight.set(cacheKey, promise)
  return promise
}

const StockLogo: React.FC<StockLogoProps> = ({
  symbol,
  className = '',
  size: _size = 60,
  fallbackIcon,
  currency = 'USD'
}) => {
  // Seed from the cache synchronously so a symbol that was already resolved
  // (e.g. on a previous page) renders instantly instead of flashing the
  // loading skeleton every time this component remounts on navigation.
  const initialCached = getFreshCachedEntry(symbol, currency)
  const [logoUrl, setLogoUrl] = useState<string | null>(initialCached ? initialCached.url : null)
  const [loading, setLoading] = useState(!initialCached)
  const [error, setError] = useState(initialCached ? !initialCached.url : false)

  useEffect(() => {
    if (!symbol) return

    let cancelled = false

    const fetchLogo = async () => {
      try {
        const cacheKey = buildCacheKey(symbol, currency)
        const cached = loadCacheEntry(cacheKey)

        if (cached && isFresh(cached)) {
          setLogoUrl(cached.url)
          setError(!cached.url)
          setLoading(false)
          return
        }

        setLoading(true)
        setError(false)
        setLogoUrl(null) // Reset logo URL

        const apiKey = import.meta.env.VITE_LOGOKIT_API_KEY
        if (!apiKey) {
          console.warn('LogoKit API key not found')
          setError(true)
          setLoading(false)
          return
        }

        const logoKitUrl = `https://img.logokit.com/ticker/${encodeURIComponent(cacheKey)}?token=${apiKey}`
        const url = await fetchLogoUrl(cacheKey, logoKitUrl)

        if (cancelled) return

        if (url) {
          setLogoUrl(url)
          setLoading(false)
        } else {
          setError(true)
          setLoading(false)
        }
      } catch (err) {
        console.error('Error in fetchLogo:', err)
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
      }
    }

    fetchLogo()

    return () => {
      cancelled = true
    }
  }, [symbol, currency])

  const logoSize = 32
  const r = 6

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 dark:bg-gray-700 animate-pulse flex-shrink-0 ${className}`}
        style={{ width: logoSize, height: logoSize, borderRadius: r }}
      >
        <div className="bg-gray-300 dark:bg-gray-600 rounded" style={{ width: logoSize * 0.6, height: logoSize * 0.6 }} />
      </div>
    )
  }

  if (error || !logoUrl) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0 ${className}`}
        style={{ width: logoSize, height: logoSize, borderRadius: r }}
        title={`Logo not found for ${symbol}`}
      >
        {fallbackIcon || <Building2 size={logoSize * 0.5} />}
      </div>
    )
  }

  return (
    <div
      className={`flex items-center justify-center overflow-hidden bg-white flex-shrink-0 ${className}`}
      style={{ width: logoSize, height: logoSize, borderRadius: r }}
      title={`${symbol} logo`}
    >
      <img
        src={logoUrl}
        alt={`${symbol} logo`}
        className="w-full h-full object-contain"
        onError={() => { setError(true); setLogoUrl(null) }}
        loading="lazy"
      />
    </div>
  )
}

export default StockLogo